import { useCallback, useEffect, useRef } from 'react'

import { useLazySearchMapsQuery } from '~map-search/services/map'
import { useInfiniteSearchMapsState } from '~map-search/states/infiniteScroll'
import useCoords from '../useCoords'

import { useSort } from '~map-search/hooks/useSort'
import { useTagIds } from '~map-search/hooks/useTagIds'

let endReachedRequests = []
const useInfiniteSearchMapsQuery = ({ initialNumToRender, skip }) => {
  const coords = useCoords()

  const [trigger, { isError, isFetching, isLoading, isSuccess, error }] =
    useLazySearchMapsQuery()

  const { data, hasMoreItems } = useInfiniteSearchMapsState()

  const feedType = useSort()
  const tagIds = useTagIds()

  const getItems = useCallback(
    ({ from, size }) => {
      return trigger({
        from,
        size,
        feedType,
        tagIds,
        ...(feedType === 'near' && {
          coords
        })
      })
    },
    [coords, feedType, tagIds, trigger]
  )

  // Wait for skip and run only once
  const once = useRef(true)
  useEffect(() => {
    if (!skip && once.current) {
      once.current = false
      getItems({ from: 0, size: initialNumToRender })
    }
  }, [getItems, initialNumToRender, skip])

  // Watch for query tags or sort changes then
  // make query request to get items
  // and abort those request on router updates
  useEffect(() => {
    let result
    if (!once.current) {
      endReachedRequests = []
      result = getItems({ from: 0, size: initialNumToRender })
    }

    return () => {
      result?.abort()
      endReachedRequests.forEach((abort) => abort())
    }
    // We want to check if the tagIds or feedType has changed
  }, [tagIds, tagIds.length, feedType, getItems, initialNumToRender])

  const getItemsAbortController = (params) => {
    const result = getItems(params)
    endReachedRequests.push(result.abort)
  }

  return {
    getItems: getItemsAbortController,
    data,
    hasMoreItems,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    error
  }
}

export { useInfiniteSearchMapsQuery }
export default useInfiniteSearchMapsQuery
